import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';

import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { FlexRow } from 'ui-components/components/flex';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import messages from './messages';
import style from './order-source-deactivation-modal.module.less';
import { OrderSourceTable } from './order-source-table/order-source-table';
import ModalPortal from '../../../../components/modal-portal';
import { PromotionalBanner } from '../../../../components/promotional-banner';
import { useGlobalModalLaunchContext } from '../../../../contexts/modal-context/global-modal-context';
import { UNLIMITED_PLAN_LIMIT } from '../../payment-and-subscription/hooks/use-plan-details';

const MODAL_HEADER_ID = 'order-source-deactivation-modal-header';

/**
 * Modal that allows users to deactivate order sources when their plan only allows
 * a limited number of active order sources. Shows current active order sources and allows
 * users to deactivate sources to stay within their plan limits.
 * @param {object} props
 * @param {()=>void} props.onContinue
 * @param {Array} props.orderSources
 * @param {number} props.maxAllowedOrderSources
 * @param {boolean} props.isOrderSourceLimitExceeded
 * @returns {React.JSX.Element} The OrderSourceDeactivationModal component
 */
export const OrderSourceDeactivationModal = ({ isOrderSourceLimitExceeded, onContinue, orderSources = [], maxAllowedOrderSources = 1 }) => {
  const activeOrderSourceCount = useMemo(() => {
    const activeOrderSources = orderSources.filter((store) => store.isActive === true);
    return activeOrderSources.length;
  }, [orderSources]);

  const { openManageSubscriptionDrawer } = useGlobalModalLaunchContext();

  const onClickUpgrade = useCallback(() => {
    openManageSubscriptionDrawer();
  }, [openManageSubscriptionDrawer]);

  const maxOrderSourcesText =
    maxAllowedOrderSources === UNLIMITED_PLAN_LIMIT ? (
      <FormattedMessage {...messages.unlimited} />
    ) : (
      <FormattedNumber value={maxAllowedOrderSources} />
    );

  return (
    <ModalPortal hideCloseButton onClose={noop}>
      <Modal modalHeaderId={MODAL_HEADER_ID} size='medium'>
        <ModalHeader id={MODAL_HEADER_ID} preventClose>
          <Text textType={TextType.H3} value={messages.removeOrderSourceHeader} />
        </ModalHeader>
        <ModalBody className={style['modal-body']}>
          <PromotionalBanner
            promotionalHeaderMessage={<FormattedMessage {...messages.planLimitWarning} values={{ maxAllowedOrderSources }} />}
            promotionalSubheaderMessage={<FormattedMessage {...messages.deactivateInstructions} values={{ maxAllowedOrderSources }} />}
          >
            <Button onClick={onClickUpgrade} theme={THEME_CONSTANTS.promotional}>
              <FormattedMessage {...messages.upgradeButton} />
            </Button>
          </PromotionalBanner>
          <FlexRow className={style['order-source-counts-container']}>
            <Text
              className={style['max-order-sources']}
              element='span'
              formattedMessageValues={{ maxAllowedOrderSources: maxOrderSourcesText }}
              textType={TextType.Label}
              value={messages.maxAllowedOrderSources}
            />
            <FlexRow className={style['current-count-container']}>
              <Text
                element='span'
                formattedMessageValues={{ currentActiveOrderSources: <FormattedNumber value={activeOrderSourceCount} /> }}
                textType={TextType.Label}
                value={messages.currentActiveOrderSources}
              />
              {isOrderSourceLimitExceeded ? (
                <FontAwesomeIcon className={style['error-icon']} icon={faExclamationCircle} title='ExclamationIcon' />
              ) : (
                <FontAwesomeIcon className={style['success-icon']} icon={faCheckCircle} title='CheckIcon' />
              )}
            </FlexRow>
          </FlexRow>
          <OrderSourceTable orderSources={orderSources} />
        </ModalBody>
        <ModalFooter>
          <Button disabled={isOrderSourceLimitExceeded} onClick={onContinue} theme={THEME_CONSTANTS.primary}>
            <FormattedMessage {...messages.continueButton} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
